// https://github.com/dpiccone/ditherjs/blob/master/lib/algorithms/atkinsonDither.js

const colorDistance = (a: number[], b: number[]) => {
	return Math.sqrt(
		Math.pow(((a[0]) - (b[0])), 2) +
		Math.pow(((a[1]) - (b[1])), 2) +
		Math.pow(((a[2]) - (b[2])), 2)
	);
};

const approximateColor = function approximateColor(color: number[], palette: number[][]) {

	const findIndex: any = (fun: (a: number[], b: number[]) => number, arg: number[], list: number[][], min: number[]) => {

		if (list.length === 2) {

			if (fun(arg, min) <= fun(arg, list[1])) {
				return min;
			} else {
				return list[1];
			}
		} else {

			const tl = list.slice(1);
			if (fun(arg, min) > fun(arg, list[1])) {
				min = list[1];
			}
			return findIndex(fun, arg, tl, min);
		}
	};

	const foundColor = findIndex(colorDistance, color, palette, palette[0]);
	return foundColor;
};

const atkinsonDither = (uint8data: Uint8ClampedArray, height: number, width: number) => {

	const step = 1;
	const palette = [[0, 0, 0], [255, 255, 255]];
	const d = new Uint8ClampedArray(uint8data);
	const out = new Uint8ClampedArray(uint8data);
	const ratio = 1 / 8;

	const $i = function (x: number, y: number) {
		return (4 * x) + (4 * y * width);
	};

	let r, g, b, q, i, color, approx, tr, tg, tb, dx, dy, di;

	for (let y = 0; y < height; y += step) {
		for (let x = 0; x < width; x += step) {
			i = (4 * x) + (4 * y * width);

			// Define bytes
			r = i;
			g = i + 1;
			b = i + 2;

			color = [d[r], d[g], d[b]];
			approx = approximateColor(color, palette);

			q = [];
			q[r] = d[r] - approx[0];
			q[g] = d[g] - approx[1];
			q[b] = d[b] - approx[2];

			// Diffuse the error for three colors
			d[$i(x + step, y) + 0] += ratio * q[r];
			d[$i(x - step, y + step) + 0] += ratio * q[r];
			d[$i(x, y + step) + 0] += ratio * q[r];
			d[$i(x + step, y + step) + 0] += ratio * q[r];
			d[$i(x + (2 * step), y) + 0] += ratio * q[r];
			d[$i(x, y + (2 * step)) + 0] += ratio * q[r];

			d[$i(x + step, y) + 1] += ratio * q[g];
			d[$i(x - step, y + step) + 1] += ratio * q[g];
			d[$i(x, y + step) + 1] += ratio * q[g];
			d[$i(x + step, y + step) + 1] += ratio * q[g];
			d[$i(x + (2 * step), y) + 1] += ratio * q[g];
			d[$i(x, y + (2 * step)) + 1] += ratio * q[g];

			d[$i(x + step, y) + 2] += ratio * q[b];
			d[$i(x - step, y + step) + 2] += ratio * q[b];
			d[$i(x, y + step) + 2] += ratio * q[b];
			d[$i(x + step, y + step) + 2] += ratio * q[b];
			d[$i(x + (2 * step), y) + 2] += ratio * q[b];
			d[$i(x, y + (2 * step)) + 2] += ratio * q[b];

			tr = approx[0];
			tg = approx[1];
			tb = approx[2];

			// Draw a block
			for (dx = 0; dx < step; dx++) {
				for (dy = 0; dy < step; dy++) {
					di = i + (4 * dx) + (4 * width * dy);

					// Draw pixel
					out[di] = tr;
					out[di + 1] = tg;
					out[di + 2] = tb;
				}
			}
		}
	}
	return out;
};


type processImageFn = (context: CanvasRenderingContext2D, image: HTMLImageElement, destWidth: number, destHeight: number) => void;

const processImage = (image: Blob, destWidth: number, destHeight: number, format: string, processFn: processImageFn): Promise<Blob> => {

	const img: HTMLImageElement = document.createElement('img');
	const canvas: HTMLCanvasElement = document.createElement('canvas');
	const context: Nullable<CanvasRenderingContext2D> = canvas.getContext('2d');
	let imageUrl = '';

	return new Promise<Blob>((resolve, _reject) => {
		img.onload = () => {
			canvas.width = destWidth;
			canvas.height = destHeight;

			if (context !== null) {
				processFn(context, img, destWidth, destHeight);
			}

			URL.revokeObjectURL(imageUrl);

			canvas.toBlob((resultBlob: Nullable<Blob>) => {
				if (resultBlob)
					resolve(resultBlob);
			}, format, 0.8 /* quality*/);
		};

		imageUrl = URL.createObjectURL(image);
		img.src = imageUrl;
	});
};

const resizeImage = (context: CanvasRenderingContext2D, image: HTMLImageElement, destWidth: number, destHeight: number) => {
	context.drawImage(image, 0, 0, destWidth, destHeight);
};

const ditherImage = (context: CanvasRenderingContext2D, image: HTMLImageElement, destWidth: number, destHeight: number) => {

	const ditherImageData = (imageData: ImageData, width: number, height: number) => {
		const data = imageData.data;
		imageData.data.set(atkinsonDither(data, height, width));
		return imageData;
	};

	context.drawImage(image, 0, 0, destWidth, destHeight);
	const imageData = context.getImageData(0, 0, image.width, image.height);
	context.putImageData(ditherImageData(imageData, image.height, image.width), 0, 0);
};

export const resizeImageBlob = (image: Blob, width: number, height: number) => processImage(image, width, height, 'image/png', resizeImage);
export const ditherImageBlob = (image: Blob, width: number, height: number) => processImage(image, width, height, 'image/png', ditherImage);

export const convertImageBlobToJpeg = (image: Blob, width: number, height: number) => processImage(image, width, height, 'image/jpeg', (context: CanvasRenderingContext2D, image: HTMLImageElement, destWidth: number, destHeight: number) => {
	context.fillStyle = 'white';
	context.fillRect(0, 0, destWidth, destHeight);
	context.drawImage(image, 0, 0, destWidth, destHeight);
});

export const getImageBlobSize = async (image: Blob) => {
	const size = { width: 0, height: 0 };
	await processImage(image, 100, 100, 'image/png', (_context, imageElement) => {
		size.width = imageElement.width;
		size.height = imageElement.height;
	});
	return size;
};
